import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Ability, Button, Icon } from '..';
import { Quote } from '../../models';
import type { QuoteItem } from '../../models';
import { useAppDispatch } from '../../redux';
import { loadQuote, updateQuote } from '../../redux';
import ClientAreaApi from '../../services/ClientArea';
import { formatPrice } from '../../locales';

export type CatalogItem = {
    id: number;
    name: string;
    shortDescription?: string | null;
    fullDescription?: string | null;
    itemgroupId?: number | null;
    itemgroupName?: string | null;
    renewPeriod?: number | null;
};

type QuoteZoneClientSalesProps = {
    quote: Quote;
};

const SYNCABLE_STATUSES = [
    Quote.STATUS_ACCEPTED,
    Quote.STATUS_ADVANCE_BILLED,
    Quote.STATUS_ADVANCE_PAID,
    Quote.STATUS_BILLED,
    Quote.STATUS_PAID,
];

const SAVING_ALL = '__all__';

function isBillableItem(item: QuoteItem) {
    if (item.excluded)
        return false;
    // Offered (free) lines can still be mapped/synced to Zone Client.
    if (item.free)
        return true;
    // Non-zero prices only: positive sales and negative remises/credits.
    // Zero-price non-free lines are group separators and stay excluded.
    return Number(item.price || 0) !== 0;
}

function renewPeriodLabel(renewPeriod: number | null | undefined, t: (key: string) => string): string | null {
    switch (renewPeriod) {
        case 0: return t('quotes.renew_period_never');
        case 1: return t('quotes.renew_period_monthly');
        case 2: return t('quotes.renew_period_annual');
        case 3: return t('quotes.renew_period_one_time');
        case 4: return t('quotes.renew_period_quarterly');
        default: return null;
    }
}

function billingPeriodFromRenewPeriod(
    renewPeriod: number | null | undefined
): 'oneTime' | 'monthly' | 'quarterly' | 'annual' {
    if (renewPeriod === 1)
        return 'monthly';
    if (renewPeriod === 2)
        return 'annual';
    if (renewPeriod === 4)
        return 'quarterly';
    return 'oneTime';
}

function catalogOptionLabel(item: CatalogItem, t: (key: string) => string) {
    const name = item.name || String(item.id);
    // Prefer fullDescription: Zone Client often leaves shortDescription empty
    // (or uses placeholders like "domain_name") while fullDescription holds the
    // human label that distinguishes homonyms (e.g. Frais/bonification).
    const detail = (item.fullDescription || item.shortDescription || '').trim();
    const period = renewPeriodLabel(item.renewPeriod, t);
    const base = detail ? `${name} — ${detail}` : name;
    return period ? `${base} (${period})` : base;
}

function mapLineToArticle(line: QuoteItem, selected: CatalogItem | undefined, selectedId: number | null): QuoteItem {
    return {
        ...line,
        zoneClientItemId: selectedId,
        zoneClientItemName: selected?.name || null,
        billingPeriod: selected
            ? billingPeriodFromRenewPeriod(selected.renewPeriod)
            : line.billingPeriod,
        zoneClientItemposId: selectedId === line.zoneClientItemId
            ? line.zoneClientItemposId
            : null,
        zoneClientExternalRef: selectedId === line.zoneClientItemId
            ? line.zoneClientExternalRef
            : null,
        zoneClientSyncedAt: selectedId === line.zoneClientItemId
            ? line.zoneClientSyncedAt
            : null,
        zoneClientSyncError: null,
    };
}

function QuoteZoneClientSales({ quote }: QuoteZoneClientSalesProps) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const [catalog, setCatalog] = useState<CatalogItem[]>([]);
    const [loadingCatalog, setLoadingCatalog] = useState(false);
    const [catalogError, setCatalogError] = useState<string | null>(null);
    const [savingItemId, setSavingItemId] = useState<string | null>(null);
    const [assignAllValue, setAssignAllValue] = useState('');
    const [syncing, setSyncing] = useState(false);
    const [syncReport, setSyncReport] = useState<string | null>(null);
    const [syncError, setSyncError] = useState<string | null>(null);

    const canSync = SYNCABLE_STATUSES.includes(quote.status);
    const billableItems = useMemo(
        () => (quote.items || []).filter(isBillableItem),
        [quote.items]
    );
    const unsyncedItems = useMemo(
        () => billableItems.filter((item) => !item.zoneClientItemposId),
        [billableItems]
    );
    const pendingItems = useMemo(
        () => billableItems.filter((item) => item.zoneClientItemId && !item.zoneClientItemposId),
        [billableItems]
    );
    const pendingTotal = useMemo(
        () => pendingItems.reduce((sum, item) => sum + Number(item.price || 0), 0),
        [pendingItems]
    );

    useEffect(() => {
        if (!canSync)
            return;

        let cancelled = false;
        setLoadingCatalog(true);
        setCatalogError(null);
        ClientAreaApi.getCatalog()
            .then((data) => {
                if (!cancelled)
                    setCatalog(data.items || []);
            })
            .catch((err: Error) => {
                if (!cancelled)
                    setCatalogError(err.message || 'catalog_failed');
            })
            .finally(() => {
                if (!cancelled)
                    setLoadingCatalog(false);
            });

        return () => { cancelled = true; };
    }, [canSync, quote._id]);

    const groupedOptions = useMemo(() => {
        const groups = new Map<string, CatalogItem[]>();
        for (const item of catalog) {
            const key = item.itemgroupName || t('quotes.zone_client_ungrouped');
            if (!groups.has(key))
                groups.set(key, []);
            groups.get(key)!.push(item);
        }
        return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
    }, [catalog, t]);

    const catalogSelectOptions = (
        <>
            {groupedOptions.map(([groupName, items]) => (
                <optgroup key={groupName} label={groupName}>
                    {items.map((c) => (
                        <option key={c.id} value={c.id}>
                            {catalogOptionLabel(c, t)}
                        </option>
                    ))}
                </optgroup>
            ))}
        </>
    );

    if (!canSync || billableItems.length === 0)
        return null;

    const saveMappedItems = (nextItems: QuoteItem[], savingId: string) => {
        setSavingItemId(savingId);
        setSyncReport(null);
        setSyncError(null);

        // patch=true: send a lean quote payload (avoid re-wrapping Project/Client models)
        const payload = {
            _id: quote._id,
            name: quote.name,
            project: typeof quote.project === 'object' ? quote.project?._id : quote.project,
            client: typeof quote.client === 'object' ? quote.client?._id : quote.client,
            needsAdvance: quote.needsAdvance,
            status: quote.status,
            items: nextItems.map((line) => ({
                ...line,
                change: typeof line.change === 'object' ? line.change?._id : line.change,
            })),
        };

        dispatch(updateQuote(payload, true, false, (err: Error | null) => {
            if (err)
                setSyncError(err.message || t('quotes.zone_client_map_failed'));
            setSavingItemId(null);
        }));
    };

    const mapArticle = (item: QuoteItem, articleId: string) => {
        if (!item._id)
            return;

        const selectedId = articleId ? Number(articleId) : null;
        const selected = catalog.find((c) => c.id === selectedId);
        const nextItems = (quote.items || []).map((line) => {
            if (line._id !== item._id)
                return line;
            return mapLineToArticle(line, selected, selectedId);
        });

        saveMappedItems(nextItems, item._id);
    };

    const mapAllArticles = (articleId: string) => {
        setAssignAllValue('');
        if (!articleId || unsyncedItems.length === 0)
            return;

        const selectedId = Number(articleId);
        const selected = catalog.find((c) => c.id === selectedId);
        const targetIds = new Set(unsyncedItems.map((item) => item._id));
        const nextItems = (quote.items || []).map((line) => {
            if (!targetIds.has(line._id))
                return line;
            return mapLineToArticle(line, selected, selectedId);
        });

        saveMappedItems(nextItems, SAVING_ALL);
    };

    const createSales = async () => {
        if (syncing)
            return;
        setSyncing(true);
        setSyncError(null);
        setSyncReport(null);
        try {
            const report = await ClientAreaApi.syncQuoteSales(quote._id);
            const created = report.synced || 0;
            const failed = report.failed || 0;
            const skipped = report.skipped || 0;
            setSyncReport(t('quotes.zone_client_sync_report', { created, failed, skipped }));
            dispatch(loadQuote(quote._id));
        } catch (err) {
            console.error(err);
            setSyncError((err as Error).message || t('quotes.zone_client_sync_failed'));
        } finally {
            setSyncing(false);
        }
    };

    const mappedCount = billableItems.filter((i) => i.zoneClientItemId).length;
    const syncedCount = billableItems.filter((i) => i.zoneClientItemposId).length;
    const pendingMapped = pendingItems.length;
    const isSaving = !!savingItemId;

    const clientZoneId = quote.client?.id;
    const futureInvoiceUrl = clientZoneId
        ? `https://clients.netoxygen.ch/futureinvoice_details.php?contactId=${clientZoneId}`
        : null;

    const createSalesTitle = pendingMapped > 0
        ? t('quotes.zone_client_create_sales_with_total', {
            count: pendingMapped,
            total: formatPrice(pendingTotal),
        })
        : t('quotes.zone_client_create_sales');

    return (
        <Ability can="quote:changeStep">
            <div className="mt-5 col-span-1 flex flex-col space-y-3 print:hidden">
                <h3 className="text-xl font-bold">
                    {t('quotes.zone_client_sales')}
                </h3>
                {quote.partialAcceptance && (
                    <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2 text-sm">
                        {t('quotes.zone_client_partial_hint')}
                    </div>
                )}
                <p className="text-sm text-gray-600 dark:text-gray-300">
                    {t('quotes.zone_client_sales_help')}
                </p>

                {loadingCatalog && (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Icon type="loading" size={4} />
                        {t('quotes.zone_client_loading_catalog')}
                    </div>
                )}
                {catalogError && (
                    <div className="rounded-md bg-red-50 border border-red-200 text-red-700 px-3 py-2 text-sm">
                        {t('quotes.zone_client_catalog_error')}: {catalogError}
                    </div>
                )}

                {unsyncedItems.length > 0 && (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-200 shrink-0">
                            {t('quotes.zone_client_assign_all_label')}
                        </label>
                        <div className="sm:w-80 flex items-center gap-2">
                            <select
                                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm px-2 py-1.5"
                                value={assignAllValue}
                                disabled={isSaving || loadingCatalog}
                                onChange={(e) => mapAllArticles(e.target.value)}
                            >
                                <option value="">{t('quotes.zone_client_assign_all')}</option>
                                {catalogSelectOptions}
                            </select>
                            {savingItemId === SAVING_ALL && <Icon type="loading" size={4} />}
                        </div>
                    </div>
                )}

                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md divide-y divide-gray-100 dark:divide-gray-700">
                    {billableItems.map((item) => (
                        <div key={item._id} className="p-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <div className="min-w-0 flex-1">
                                <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                                    <span dangerouslySetInnerHTML={{ __html: item.title || '' }} />
                                </div>
                                <div className="text-sm text-gray-500">
                                    {item.free ? t('quotes.free_item') : formatPrice(item.price)}
                                    {item.zoneClientItemposId && (
                                        <span className="ml-2 text-green-700 dark:text-green-400">
                                            · {t('quotes.zone_client_synced', { id: item.zoneClientItemposId })}
                                        </span>
                                    )}
                                    {item.zoneClientSyncError && (
                                        <span className="ml-2 text-red-600">
                                            · {item.zoneClientSyncError}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="sm:w-80 flex items-center gap-2">
                                <select
                                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm px-2 py-1.5"
                                    value={item.zoneClientItemId ?? ''}
                                    disabled={savingItemId === item._id || isSaving || loadingCatalog || !!item.zoneClientItemposId}
                                    onChange={(e) => mapArticle(item, e.target.value)}
                                >
                                    <option value="">{t('quotes.zone_client_pick_article')}</option>
                                    {catalogSelectOptions}
                                </select>
                                {savingItemId === item._id && <Icon type="loading" size={4} />}
                            </div>
                        </div>
                    ))}
                    <div className="p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 bg-gray-50 dark:bg-gray-900/40">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                            {t('quotes.zone_client_pending_total', {
                                count: pendingMapped,
                                total: formatPrice(pendingTotal),
                            })}
                        </span>
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                    <Button
                        color="primary"
                        icon={syncing ? 'loading' : 'check'}
                        title={createSalesTitle}
                        onClick={createSales}
                        disabled={syncing || pendingMapped === 0 || isSaving}
                    />
                    <span className="text-sm text-gray-500">
                        {t('quotes.zone_client_progress', {
                            mapped: mappedCount,
                            synced: syncedCount,
                            total: billableItems.length,
                        })}
                    </span>
                    {futureInvoiceUrl && (
                        <a
                            href={futureInvoiceUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm text-blue-600 hover:underline"
                        >
                            {t('quotes.zone_client_open_future_invoice')}
                        </a>
                    )}
                </div>

                {syncReport && (
                    <div className="rounded-md bg-green-50 border border-green-200 text-green-800 px-3 py-2 text-sm">
                        {syncReport}
                    </div>
                )}
                {syncError && (
                    <div className="rounded-md bg-red-50 border border-red-200 text-red-700 px-3 py-2 text-sm">
                        {syncError}
                    </div>
                )}
            </div>
        </Ability>
    );
}

export default QuoteZoneClientSales;
